// src/pages/commercial/CommercialInvoicesPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';
import InvoiceForm from '../../components/admin/billing/invoice/InvoiceForm';
import PDFGenerator from '../../utils/PDFGenerator';

export default function CommercialInvoicesPage() {
    const { user } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [vehicles, setVehicles] = useState([]); // Projects
    const [quotes, setQuotes] = useState([]); // Linked quotes
    const [services, setServices] = useState([]);
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showForm, setShowForm] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [search, setSearch] = useState('');

    // Settings for defaults (fetched once)
    const [settings, setSettings] = useState({ invoice_prefix: 'FACT', invoice_counter: 1, default_tax_rate: 20, invoice_payment_terms: 30 });
    const [settingsId, setSettingsId] = useState(null);

    // Share / QR Code
    const [showQr, setShowQr] = useState(false);
    const [qrUrl, setQrUrl] = useState('');
    const companyName = 'INFOCOLAB';
    const buildShareText = (link) => (
        `Bonjour,\n\n` +
        `pour créer votre dossier client et démarrer votre projet, merci de compléter ce formulaire : ${link}\n` +
        `Cela prend moins de 3 minutes.\n` +
        `Si vous avez la moindre question, répondez simplement à ce message — nous sommes là pour vous accompagner.\n\n` +
        `Bien à vous,\n` +
        `L’équipe ${companyName}`
    );

    const fetchData = async () => {
        setLoading(true);
        try {
            // Load Customers
            try {
                const cRes = await pb.collection('customers').getFullList({ sort: '-created', filter: `created_by = "${user.id}" || commercial_id = "${user.id}"` });
                setCustomers(cRes);
            } catch (e) {
                console.error("Error loading customers", e);
            }

            // Load Projects
            try {
                const pRes = await pb.collection('projects').getFullList({
                    sort: '-created',
                    filter: `created_by = "${user.id}" || client.commercial_id = "${user.id}"`,
                    requestKey: null
                });
                setVehicles(pRes);
            } catch (e) {
                console.warn("Filtered projects load failed, fallback to own projects.", e);
                try {
                    const pResFallback = await pb.collection('projects').getFullList({ sort: '-created', filter: `created_by = "${user.id}"` });
                    setVehicles(pResFallback);
                } catch (ex) { console.error("Error loading projects fallback", ex); }
            }

            // Load Invoices & Quotes
            try {
                const [iRes, qRes] = await Promise.all([
                    pb.collection('invoices').getFullList({ sort: '-created', expand: 'customer_id,project_id', filter: `customer_id.commercial_id = "${user.id}"` }),
                    pb.collection('quotes').getFullList({ sort: '-created', filter: `customer_id.commercial_id = "${user.id}"` })
                ]);
                setInvoices(iRes);
                setQuotes(qRes);
            } catch (e) { console.error("Error loading invoices/quotes", e); }

            // Load Catalog & Settings (Non-critical)
            try {
                const [sRes, prodRes, setRes] = await Promise.all([
                    pb.collection('services_catalog').getFullList({ sort: 'name' }),
                    pb.collection('products').getFullList({ sort: 'name' }),
                    pb.collection('settings').getList(1, 1, { sort: '-created' })
                ]);
                setServices(sRes.map(s => ({ id: s.id, name: s.name || s.title || 'Service', base_price: Number(s.base_price || s.price || 0), active: s.active !== false })));
                setParts(prodRes.map(p => ({ id: p.id, name: p.name || p.title || 'Produit', price: Number(p.price || p.base_price || 0), sku: p.sku || '' })));

                if (setRes?.items?.[0]) {
                    setSettings({ ...setRes.items[0], _baseUrl: pb?.baseUrl });
                    setSettingsId(setRes.items[0].id);
                }
            } catch (e) { console.error("Error loading catalog/settings", e); }

        } catch (err) {
            console.error("Fatal error fetching data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const refreshInvoices = async () => {
        const iRes = await pb.collection('invoices').getFullList({ sort: '-created', expand: 'customer_id,project_id', filter: `customer_id.commercial_id = "${user.id}"` });
        setInvoices(iRes);
    };

    // --- FORM HANDLERS ---
    const handleCreate = async (data) => {
        setFormLoading(true);
        try {
            const payload = { ...data };
            if (!payload.invoice_number) {
                const prefix = settings?.invoice_prefix || 'FACT';
                const next = String((settings?.invoice_counter || 1)).padStart(4, '0');
                payload.invoice_number = `${prefix}-${next}`;
            }
            // Project fallback
            if (payload.vehicle_id && !payload.project_id) payload.project_id = payload.vehicle_id;

            // Remove UI flags
            delete payload.allowEmpty;
            delete payload.installments;

            await pb.collection('invoices').create(payload);

            if (settingsId) {
                await pb.collection('settings').update(settingsId, {
                    invoice_counter: Number(settings.invoice_counter || 1) + 1
                });
            }

            alert("Facture créée !");
            setShowForm(false);
            setEditingInvoice(null);
            refreshInvoices();
            setSettings(prev => ({ ...prev, invoice_counter: Number(prev.invoice_counter) + 1 }));

        } catch (err) {
            console.error(err);
            alert("Erreur lors de la création : " + err.message);
        } finally {
            setFormLoading(false);
        }
    };

    const handleUpdate = async (data) => {
        setFormLoading(true);
        try {
            const payload = { ...data };
            if (payload.vehicle_id && !payload.project_id) payload.project_id = payload.vehicle_id;
            delete payload.installments;

            await pb.collection('invoices').update(editingInvoice.id, payload);
            alert("Facture mise à jour !");
            setShowForm(false);
            setEditingInvoice(null);
            refreshInvoices();
        } catch (err) {
            console.error(err);
            alert("Erreur mise à jour : " + err.message);
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cette facture ?")) return;
        try {
            await pb.collection('invoices').delete(id);
            refreshInvoices();
        } catch (err) {
            alert("Erreur suppression: " + err.message);
        }
    };

    const handleGeneratePDF = async (invoice, mode = 'preview') => {
        try {
            let customer = customers.find(c => c.id === invoice.customer_id) || invoice.expand?.customer_id;
            let project = vehicles.find(v => v.id === (invoice.project_id || invoice.vehicle_id));

            const generator = new PDFGenerator();
            await generator.generateInvoicePDF(invoice, customer, project, settings, []);
            if (mode === 'download') generator.download(`Facture_${invoice.invoice_number}.pdf`);
            else generator.preview();
        } catch (err) {
            console.error(err);
            alert("Erreur PDF");
        }
    };

    const statusLabel = (inv) => {
        const { payment_status, status, due_date } = inv;
        let finalStatus = payment_status || 'pending';

        if (finalStatus !== 'paid' && due_date && new Date(due_date) < new Date()) {
            finalStatus = 'overdue';
        }
        if (status === 'draft') finalStatus = 'draft';

        const map = {
            draft: 'Brouillon',
            pending: 'En attente',
            paid: 'Payée',
            partial: 'Partiel',
            overdue: 'En retard',
            cancelled: 'Annulée'
        };

        return map[finalStatus] || finalStatus;
    };

    const filteredInvoices = invoices.filter(inv =>
        (inv.invoice_number?.toLowerCase() || '').includes(search.toLowerCase()) ||
        (inv.expand?.customer_id?.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (inv.expand?.customer_id?.company || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <CommercialLayout title="">
            <div className="commercial-page-wrapper">
                <style>{`
                /* Adapting Admin Styles to Commercial Layout - Scoped */
                .commercial-page-wrapper {
                    --admin-panel: var(--commercial-panel, #fff);
                    --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                    --admin-border: var(--commercial-border, #e2e8f0);
                    --admin-text: var(--commercial-text, #0f172a);
                    --admin-muted: var(--commercial-muted, #64748b);
                    --admin-accent: var(--commercial-accent, #4f46e5);
                }
                
                .commercial-page-wrapper .btn-secondary { background: transparent; border: 1px solid var(--admin-border); color: var(--admin-text); padding: 8px 16px; border-radius: 8px; cursor: pointer; font-weight: 500; }
                .commercial-page-wrapper .btn-secondary:hover { background: rgba(0,0,0,0.03); }

                .commercial-page-wrapper .btn-accent-soft { background: rgba(79,70,229,0.12); color: var(--admin-accent); border: 1px solid rgba(79,70,229,0.45); }
                .commercial-page-wrapper .btn-accent-soft:hover { background: rgba(79,70,229,0.22); border-color: rgba(79,70,229,0.6); }
                
                .commercial-page-wrapper .btn-primary { 
                    display: inline-flex; align-items: center; gap: 10px;
                    padding: 10px 14px; border-radius: 10px;
                    background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85));
                    border: 1px solid rgba(79,70,229,0.6);
                    box-shadow: 0 6px 18px rgba(79,70,229,0.35);
                    color: white;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-weight: 500;
                }
                .commercial-page-wrapper .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
                .commercial-page-wrapper .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }

                .commercial-page-wrapper .invoice-card {
                    border: 1px solid var(--admin-border);
                    border-radius: 12px;
                    padding: 16px;
                    background: linear-gradient(180deg, var(--admin-panel), var(--admin-panel-2));
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .commercial-page-wrapper .invoice-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
                }

                .commercial-page-wrapper .customer-avatar {
                    width: 40px; height: 40px;
                    border-radius: 999px;
                    display: grid; place-items: center;
                    font-weight: 700;
                    background: var(--admin-accent);
                    color: white;
                    font-size: 1.1rem;
                }

                .commercial-page-wrapper .header-pill { cursor: pointer; border: none; background: transparent; color: var(--admin-text); padding: 6px; border-radius: 6px; transition: background 0.2s; }
                .commercial-page-wrapper .header-pill:hover { background: rgba(0,0,0,0.05); }
                .commercial-page-wrapper .header-shape { border-radius: 8px; padding: 6px 10px; border: 1px solid transparent; }
                
                .commercial-page-wrapper .btn-muted { background: var(--admin-panel); border: 1px solid var(--admin-border); }
                .commercial-page-wrapper .btn-muted:hover { border-color: var(--admin-muted); }
                
                .commercial-page-wrapper .btn-danger { background: rgba(239,68,68,0.1); color: #ef4444; border: 1px solid rgba(239,68,68,0.3); }
                .commercial-page-wrapper .btn-danger:hover { background: rgba(239,68,68,0.2); }
                
                .commercial-page-wrapper .btn-accent { background: rgba(79,70,229,0.1); color: var(--admin-accent); border: 1px solid rgba(79,70,229,0.3); }
                .commercial-page-wrapper .btn-accent:hover { background: rgba(79,70,229,0.2); }

                .commercial-page-wrapper .searchbar { position: relative; display: flex; align-items: center; }
                .commercial-page-wrapper .searchbar .icon { position: absolute; left: 10px; color: var(--admin-muted); pointer-events: none; }
                .commercial-page-wrapper .searchbar input {
                    width: 300px;
                    background: var(--admin-panel);
                    border: 1px solid var(--admin-border);
                    color: var(--admin-text);
                    border-radius: 10px;
                    padding: 10px 10px 10px 36px;
                    outline: none;
                    transition: border-color 0.15s ease;
                }
                .commercial-page-wrapper .searchbar input:focus { border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.1); }
                
                .commercial-page-wrapper .status-badge { padding: 4px 10px; border-radius: 999px; border: 1px solid var(--admin-border); font-size: 0.85rem; font-weight: 500; background: var(--admin-panel-2); color: var(--admin-text); }
                
                @media (max-width: 640px) {
                    .commercial-page-wrapper .searchbar input { width: 100%; }
                    .commercial-page-wrapper .header-top { flex-direction: column; align-items: stretch !important; gap: 16px; }
                    .commercial-page-wrapper .btn-primary { width: 100%; justify-content: center; }
                }
            `}</style>

                <div className="header-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Factures</h1>
                        <p style={{ color: 'var(--admin-muted)', margin: '4px 0 0 0' }}>Gérez la facturation et les paiements</p>
                    </div>
                    <button
                        className="btn-primary"
                        onClick={() => { setEditingInvoice(null); setShowForm(true); }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.2)' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                        </span>
                        <span>Nouvelle facture</span>
                    </button>
                </div>

                <div className="card" style={{ marginBottom: 24, padding: 12, borderRadius: 12, border: '1px solid var(--admin-border)', background: 'var(--admin-panel)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button className="header-pill header-shape" title="QR Onboarding" onClick={() => {
                                const link = window.location.origin + '/inscription?ref=' + user.id;
                                setQrUrl(link);
                                setShowQr(true);
                            }} style={{ border: '1px solid var(--admin-border)' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z" />
                                </svg>
                            </button>
                            <button className="header-pill header-shape" style={{ border: '1px solid var(--admin-border)', fontSize: '0.9rem' }} onClick={async () => {
                                const link = window.location.origin + '/inscription?ref=' + user.id;
                                try { await navigator.clipboard.writeText(link); alert('Lien copié'); } catch { }
                            }}>Copier le lien</button>
                            <button className="header-pill header-shape" style={{ border: '1px solid var(--admin-border)', fontSize: '0.9rem' }} onClick={async () => {
                                const link = window.location.origin + '/inscription?ref=' + user.id;
                                const shareText = buildShareText(link);
                                try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { }
                            }}>Copier le message</button>
                            <button className="header-pill header-shape" style={{ border: '1px solid var(--admin-border)', fontSize: '0.9rem' }} onClick={async () => {
                                const link = window.location.origin + '/inscription?ref=' + user.id;
                                const shareText = buildShareText(link);
                                if (navigator.share) {
                                    try { await navigator.share({ title: 'Inscription', text: shareText }); } catch { }
                                } else {
                                    try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { alert('Partage non supporté'); }
                                }
                            }}>Partager</button>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div className="searchbar">
                                <svg className="icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
                                <input
                                    type="text"
                                    placeholder="Rechercher une facture..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <div style={{ fontSize: '0.9rem', color: 'var(--admin-muted)', whiteSpace: 'nowrap' }}>
                                {filteredInvoices.length} facture{filteredInvoices.length > 1 ? 's' : ''}
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--admin-muted)' }}>Chargement...</div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                        {filteredInvoices.map(inv => (
                            <div key={inv.id} className="invoice-card">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <div className="customer-avatar">
                                        {(inv.expand?.customer_id?.name || 'C').charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                                        <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '1.05rem', color: 'var(--admin-text)' }}>{inv.invoice_number || 'Brouillon'}</div>
                                        <div style={{ color: 'var(--admin-muted)', fontSize: '0.9rem' }}>{inv.expand?.customer_id?.name || 'Client inconnu'}</div>
                                    </div>
                                    <div>
                                        <span className="status-badge">{statusLabel(inv)}</span>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gap: 8, fontSize: '0.9rem', color: 'var(--admin-text)', marginBottom: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.6 }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" /></svg>
                                        <span style={{ fontWeight: 600 }}>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(inv.total || 0)}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.6 }}><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19a2 2 0 002 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z" /></svg>
                                        <span>Émise le {new Date(inv.created).toLocaleDateString()}</span>
                                    </div>
                                    {inv.expand?.project_id && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.6 }}><path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" /></svg>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.expand.project_id.name}</span>
                                        </div>
                                    )}
                                </div>

                                <div style={{ paddingTop: 12, borderTop: '1px dashed var(--admin-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                    <button className="header-pill header-shape btn-muted" title="Voir PDF" onClick={() => handleGeneratePDF(inv, 'preview')}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" /></svg>
                                    </button>
                                    <button className="header-pill header-shape btn-muted" title="Télécharger PDF" onClick={() => handleGeneratePDF(inv, 'download')}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
                                    </button>
                                    <button className="header-pill header-shape btn-accent" title="Modifier" onClick={() => { setEditingInvoice(inv); setShowForm(true); }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                                    </button>
                                    <button className="header-pill header-shape btn-danger" title="Supprimer" onClick={() => handleDelete(inv.id)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                        {filteredInvoices.length === 0 && (
                            <div style={{ gridColumn: '1/-1', padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, color: 'var(--admin-muted)' }}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.3 }}><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" /></svg>
                                <span>Aucune facture trouvée pour cette recherche.</span>
                            </div>
                        )}
                    </div>
                )}

                {showForm && (
                    <InvoiceForm
                        invoice={editingInvoice}
                        customers={customers}
                        vehicles={vehicles} // Used as projects
                        quotes={quotes}
                        services={services}
                        parts={parts}
                        settings={settings}
                        onSubmit={(data) => editingInvoice ? handleUpdate(data) : handleCreate(data)}
                        onCancel={() => { setShowForm(false); setEditingInvoice(null); }}
                        loading={formLoading}
                        onCreateCustomer={async (d) => { const r = await pb.collection('customers').create(d); setCustomers([...customers, r]); return r; }}
                        onCreateVehicle={async (d) => {
                            const r = await pb.collection('projects').create({ ...d, client: d.customer_id });
                            setVehicles([...vehicles, r]);
                            return r;
                        }}
                    />
                )}
                {/* QR Modal */}
                {showQr && (
                    <div className="modal-overlay" onClick={() => setShowQr(false)}>
                        <div className="modal-content" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>QR Inscription</h3>
                                <button className="close-btn" onClick={() => setShowQr(false)}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                                </button>
                            </div>
                            <div style={{ padding: 20, display: 'grid', gap: 12, justifyItems: 'center' }}>
                                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrUrl)}`} alt="QR Code" width={220} height={220} style={{ display: 'block', borderRadius: 12, border: '1px solid var(--admin-border)', background: 'white' }} />
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <button className="header-pill header-shape btn-accent-soft" onClick={async () => { try { await navigator.clipboard.writeText(qrUrl); alert('Lien copié'); } catch { } }}>Copier le lien</button>
                                    <button className="header-pill header-shape btn-secondary" onClick={() => { try { const a = document.createElement('a'); a.href = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrUrl)}`; a.download = 'qr-inscription.png'; document.body.appendChild(a); a.click(); a.remove(); } catch { } }}>Télécharger QR</button>
                                </div>
                                <div style={{ wordBreak: 'break-all', color: 'var(--admin-muted)', textAlign: 'center', fontSize: '0.9rem' }}>{qrUrl}</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </CommercialLayout>
    );
}
